import React, { useEffect, useState, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import debounce from 'lodash/debounce';
import { useApp } from 'src/@carketa/app/AppContext';
import {
  Autocomplete,
  Checkbox,
  Stack,
  TextField,
  Select,
  SelectChangeEvent,
  MenuItem,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Box,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
// import { MuiChipsInput } from 'mui-chips-input';
import { ReconTemplateData } from 'src/types/recon/reconTemplateTypes';
import { trpc } from 'src/utils/trpc';
import { OrganizationPermission } from 'src/@carketa/components/control/OrganizationPermission';
import { FEATURE_FLAG } from 'src/server/common/permissions';
import { Dnd, ItemType } from 'src/@carketa/components/dnd/Dnd';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import SortIcon from '@mui/icons-material/Sort';
import { ReorderSideMenuDialog } from './ReorderSideMenuDialog';
import toast from 'react-hot-toast';

interface AutocompleteOptionInterface {
  value: string;
  label: string;
}

interface CardItemType extends ItemType {
  text: string;
  color: string;
}

export const defaultSalesPacketMenu = [
  {
    id: '1',
    text: 'Vehicle Details',
    section: 'vehicle-details',
  },
  {
    id: '2',
    text: 'Carketa Inspection',
    section: 'vehicle-reports',
  },
  {
    id: '3',
    text: 'Carketa Window Sticker',
    section: 'window-sticker',
  },
  {
    id: '4',
    text: 'Checklist Certificate',
    section: 'checklist-certificate',
  },
  {
    id: '5',
    text: 'Transportation',
    section: 'transportation',
  },
  {
    id: '6',
    text: 'Carketa Connect',
    section: 'carketa-connect',
  },
];

export const SettingsTable = () => {
  const { getOrganizationSettings, updateOrganizationSettings } = useApp();
  const organization_settings = getOrganizationSettings();
  // const [emails, setEmails] = useState<string[]>([]);

  const [workflowId, setWorkflowId] = useState<string>('');
  const [selectedCategories, setSeletedCategories] = useState<
    AutocompleteOptionInterface[]
  >([]);

  const { data: recon_categories } =
    trpc.recon_categories.getAllByLoggedInOrg.useQuery(undefined, {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    });

  const { data: recon_templates } =
    trpc.recon_template.getAllByOrganization.useQuery(undefined, {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    });

  const [items, setItems] = useState<any>(defaultSalesPacketMenu);

  useEffect(() => {
    if (!organization_settings) return;
    // if (organization_settings?.emailAddresses) {
    //   setEmails(organization_settings?.emailAddresses);
    // }

    if (!!organization_settings?.carketa_connect_workflow)
      setWorkflowId(organization_settings?.carketa_connect_workflow);

    if (!!organization_settings?.carketa_connect_categories) {
      const loadCategories =
        organization_settings?.carketa_connect_categories?.map((category) => {
          const reconCategory = recon_categories?.find(
            (c) => c.id === category
          );
          return {
            value: reconCategory?.id ?? '',
            label: reconCategory?.title ?? '',
          };
        });
      setSeletedCategories(loadCategories);
    }

    if (organization_settings?.sales_packet_menu) {
      setItems(organization_settings?.sales_packet_menu);
    } else {
      updateOrganizationSettings('sales_packet_menu', defaultSalesPacketMenu);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization_settings]);

  const handleNeedsAttentionDays = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateOrganizationSettings(
      'needs_attention_days',
      Number(event.target.value)
    );
  };

  const handleUseKilometers = (event: any) => {
    updateOrganizationSettings('use_kilometers', event.target.checked);
  };

  const handleChangeNeedsAttentionExcludeSaturday = (event: any) => {
    updateOrganizationSettings(
      'exclude_saturday_needs_attention_days',
      event.target.checked
    );
  };

  const handleChangeNeedsAttentionExcludeSunday = (event: any) => {
    updateOrganizationSettings(
      'exclude_sunday_needs_attention_days',
      event.target.checked
    );
  };

  const handleUsePlaceholderImageIgnore = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateOrganizationSettings(
      'use_ignored_placeholder_image',
      event.target.checked
    );
  };

  const handlePlaceholderImageIgnore = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateOrganizationSettings('ignored_placeholder_image', event.target.value);
  };

  const onDebounceSave = debounce(handlePlaceholderImageIgnore, 500);

  const categories = useMemo(() => {
    const workflow_template = recon_templates?.find(
      (template) => template?.id === workflowId
    )?.template as ReconTemplateData;
    const reconCategories = workflow_template?.categories?.map((category) => {
      const reconCategory = recon_categories?.find((c) => c.id === category.id);
      return {
        value: reconCategory?.id ?? '',
        label: reconCategory?.title ?? '',
      };
    });

    return reconCategories;
  }, [recon_templates, workflowId, recon_categories]);

  const handleWorkflowChange = (event: SelectChangeEvent) => {
    setSeletedCategories([]);
    setWorkflowId(event.target.value);

    updateOrganizationSettings(
      'carketa_connect_workflow',
      event.target.value ?? ''
    );
  };

  const handleCategoryChange = (event: any, newValue: any | null) => {
    const values = categories?.reduce((acc, c) => {
      const match = newValue.find(
        (item: AutocompleteOptionInterface) => item.value === c.value
      );
      if (match) {
        acc.push(match);
      }
      return acc;
    }, [] as AutocompleteOptionInterface[]);

    setSeletedCategories(values ?? []);

    updateOrganizationSettings(
      'carketa_connect_categories',
      values?.map((category) => category?.value)
    );
  };

  const handleChangeRemovePriceOnSalesPacket = (event: any) => {
    updateOrganizationSettings(
      'remove_price_on_sales_packet',
      event.target.checked
    );
  };

  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  const handleSideMenuOrder = (data: any) => {
    updateOrganizationSettings('side_menu_order', data);
    toast.success('Changes saved!', {
      duration: 5000,
    });
  };

  if (!organization_settings) {
    return <>Loading...</>;
  }

  return (
    <>
      <Grid display="flex" flexDirection="column" rowGap={4}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '50%' }}>Setting</TableCell>
                <TableCell sx={{ width: '50%' }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">
                  Set ODO Units
                </TableCell>
                <TableCell sx={{ width: '50%' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>Miles</Typography>
                    <Switch
                      onChange={handleUseKilometers}
                      checked={organization_settings?.use_kilometers ?? false}
                      sx={{
                        '&.MuiSwitch-root .MuiSwitch-switchBase': {
                          color: 'success.main',
                        },
                        '&.MuiSwitch-root .Mui-checked': {
                          color: 'success.main',
                        },
                        '&.MuiSwitch-root .MuiSwitch-track': {
                          backgroundColor: 'success.main',
                        },
                      }}
                    />
                    <Typography>Kilometers</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Placeholder image ignore
                </TableCell>
                <TableCell sx={{ width: '50%' }}>
                  <Stack direction={'row'}>
                    <Tooltip
                      title={
                        'This will ignore the specified image. It will not be able to be set as default while this is enabled.'
                      }
                    >
                      <Checkbox
                        checked={
                          Boolean(
                            organization_settings?.use_ignored_placeholder_image
                          ) || false
                        }
                        onChange={handleUsePlaceholderImageIgnore}
                      />
                    </Tooltip>
                    <Tooltip title={'Image that will be ignored'}>
                      <TextField
                        fullWidth
                        label="Image url"
                        size="small"
                        defaultValue={
                          organization_settings?.ignored_placeholder_image || ''
                        }
                        onChange={onDebounceSave}
                        type="string"
                        disabled={
                          !organization_settings?.use_ignored_placeholder_image
                        }
                      />
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Side Menu Configuration
                </TableCell>
                <TableCell sx={{ width: '50%' }}>
                  <Stack direction={'row'}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setOpen(true)}
                    >
                      <SortIcon sx={{ color: 'white', marginRight: '5px' }} />
                      <Box color="white">Reorder Side Menu</Box>
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell colSpan={2}>
                  <Stack>
                    Adjust Timeframe For Needs Attention
                    <Typography variant="caption">
                      No progress in _ Days
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">
                  Set Number of Days
                </TableCell>
                <TableCell sx={{ width: '50%' }}>
                  <Stack direction={'row'}>
                    <TextField
                      fullWidth
                      label="Number of Days"
                      size="small"
                      defaultValue={
                        organization_settings?.needs_attention_days ?? 5
                      }
                      onChange={handleNeedsAttentionDays}
                      type="number"
                      inputProps={{
                        step: 1,
                        min: 1,
                        max: 365,
                      }}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Exclude Saturday
                </TableCell>
                <TableCell sx={{ width: '50%' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      onChange={handleChangeNeedsAttentionExcludeSaturday}
                      checked={
                        organization_settings?.exclude_saturday_needs_attention_days ??
                        false
                      }
                    />
                  </Stack>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  Exclude Sunday
                </TableCell>
                <TableCell sx={{ width: '50%' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      onChange={handleChangeNeedsAttentionExcludeSunday}
                      checked={
                        organization_settings?.exclude_sunday_needs_attention_days ??
                        false
                      }
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <OrganizationPermission feature_flag={FEATURE_FLAG.carketa_connect}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '50%' }}>
                    Sales and Marketing
                  </TableCell>
                  <TableCell sx={{ width: '50%' }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ verticalAlign: 'top' }}
                  >
                    Link Sales Packet Leads to CRM
                  </TableCell>
                  <TableCell sx={{ width: '50%' }}>
                    <Stack direction="row" spacing={1}>
                       <MuiChipsInput
                        fullWidth
                        placeholder="Type email address and press enter"
                        value={emails}
                        onAddChip={(chip) => handleAddEmail(chip)}
                        onDeleteChip={(chip) => handleDeleteEmail(chip)}
                        addOnBlur
                        disableDeleteOnBackspace
                        hideClearAll
                      /> 
                    </Stack>
                  </TableCell>
                </TableRow> */}
                <TableRow>
                  <TableCell component="th" scope="row">
                    Remove Price on Sales Packet
                  </TableCell>
                  <TableCell sx={{ width: '50%' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Switch
                        onChange={handleChangeRemovePriceOnSalesPacket}
                        checked={
                          organization_settings?.remove_price_on_sales_packet ??
                          false
                        }
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    Reorder Sales Packet Side Menu
                  </TableCell>
                  <TableCell sx={{ width: '50%' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Dnd
                        items={items}
                        setItems={(items) => {
                          setItems(items as CardItemType[]);
                          updateOrganizationSettings(
                            'sales_packet_menu',
                            items
                          );
                        }}
                        renderContainer={(items: React.ReactNode) => {
                          return <List dense>{items}</List>;
                        }}
                        renderItem={(item: ItemType) => {
                          const itemData = item as CardItemType;
                          return (
                            <ListItem
                              sx={{
                                backgroundColor: (theme) =>
                                  theme.palette.primary.light,
                                borderRadius: 1.1,
                                cursor: 'pointer',
                                marginBottom: 2,
                              }}
                              dense
                            >
                              <ListItemIcon>
                                <Box sx={{ color: 'white' }}>
                                  <DragHandleIcon />
                                </Box>
                              </ListItemIcon>
                              <ListItemText>
                                <Typography sx={{ color: 'white' }}>
                                  {itemData.text}
                                </Typography>
                              </ListItemText>
                            </ListItem>
                          );
                        }}
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </OrganizationPermission>
        <OrganizationPermission feature_flag={FEATURE_FLAG.carketa_connect}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '50%' }}>Carketa Connect</TableCell>
                  <TableCell sx={{ width: '50%' }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ verticalAlign: 'top' }}
                  >
                    Reconditioning (Default Workflow)
                  </TableCell>
                  <TableCell sx={{ width: '50%' }}>
                    <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                      <FormControl fullWidth required sx={{ m: 1 }}>
                        <InputLabel>Workflow</InputLabel>
                        <Select
                          fullWidth
                          value={workflowId ?? ''}
                          label="Workflow"
                          onChange={handleWorkflowChange}
                        >
                          {recon_templates?.length ? (
                            recon_templates?.map((recon) => {
                              return (
                                <MenuItem key={recon.id} value={recon.id}>
                                  {recon?.title}
                                </MenuItem>
                              );
                            })
                          ) : (
                            <MenuItem key={'No Recon'} value={''}>
                              No Recon WorkFlows
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                      <FormControl fullWidth sx={{ m: 1 }}>
                        <Autocomplete
                          size="small"
                          multiple
                          id="apprasal-settings-users-autocomplete"
                          options={categories ?? []}
                          fullWidth
                          value={
                            selectedCategories as AutocompleteOptionInterface[]
                          }
                          getOptionKey={(option: AutocompleteOptionInterface) =>
                            option?.value
                          }
                          onChange={handleCategoryChange}
                          renderInput={(params) => (
                            <TextField {...params} label="Steps" />
                          )}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                          }
                        />
                      </FormControl>
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </OrganizationPermission>
      </Grid>
      {open && (
        <ReorderSideMenuDialog
          open={open}
          handleClose={handleClose}
          sideMenuOrder={handleSideMenuOrder}
          sideMenuOrderData={organization_settings?.side_menu_order}
        />
      )}
    </>
  );
};
